import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';

import { UserProvider, useUser } from './context/UserContext';
import { NotificationProvider } from './context/NotificationContext';
import { usePlayerProfile } from './hooks/playerProfile/usePlayerProfile';
import Header from './components/Header';
import Login from './components/Login';
import LandingPage from './pages/LandingPage';
import ClubDetails, { ProgramDetailsPage } from './pages/ClubDetails';
import ClubLandingPage from './pages/ClubLandingPage';
import ClubEditWizard from './pages/ClubEditWizard';
import ClubNotificationsPage from './pages/ClubNotificationsPage';
import EventsPage from './pages/EventsPage';
import TeamsPage from './pages/TeamsPage';
import NewEventPage from './pages/NewEventPage';
import EquipmentManager from './components/EquipmentManager/EquipmentManager';
import TeamItemsPage from './components/TeamItemsPage/TeamItemsPage';
import ProgramLayout from './components/Layouts/ProgramLayout';
import PlayerProfileForm from './pages/PlayerProfileForm';
import PlayerProfile from './pages/PlayerProfile/PlayerProfile';
import ProfileEdit from './pages/ProfileEdit/ProfileEdit';
import TalentDiscovery from './pages/TalentDiscovery/TalentDiscovery';
import ProgramManage from './pages/ProgramManage';
import NotificationsPage from './pages/NotificationsPage';
import TemplateManagementPage from './pages/TemplateManagementPage';
import AwardsPage from './pages/AwardsPage';
import EmailVerificationScreen from './components/EmailVerification/EmailVerificationScreen';
import OnboardingWizard from './components/Onboarding/OnboardingWizard';
import UserDiagnostic from './pages/UserDiagnostic';

function AppContent() {
  const { user, userProfile, loading, error, setHasCompletedOnboarding } = useUser();
  
  // Use the same hook as UserMenu to check for PlayerProfile
  const { profile: playerProfile, loading: profileLoading, profileNotFound, ready: profileReady } = usePlayerProfile(
    userProfile?._id,
    { sections: 'basicInfo', skipCache: true }
  );

  // Sync onboarding flag when player profile exists but server flag is missing
  React.useEffect(() => {
    if (playerProfile && !profileNotFound && userProfile && !userProfile.onboardingCompleted) {
      setHasCompletedOnboarding(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      fetch(`${backendUrl}/api/users/${userProfile._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ onboardingCompleted: true })
      }).catch(() => {});
    }
  }, [playerProfile, profileNotFound, userProfile, setHasCompletedOnboarding]);

  if (loading || profileLoading || !profileReady) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px', color: 'red' }}>
        <p>Error: {error.message}</p>
      </div>
    );
  }

  // Gate 1: Email Verification (for email/password users only)
  const needsVerification = user && 
    user.providerData.some(p => p.providerId === 'password') && 
    !user.emailVerified;

  if (needsVerification) {
    return <EmailVerificationScreen />;
  }

  // Gate 2: Onboarding check
  const needsOnboarding = user && 
    userProfile && 
    profileNotFound &&
    !userProfile.onboardingCompleted;

  return (
    <Router>
      <div className="App">
        {needsOnboarding ? (
          // Show onboarding wizard (uses useNavigate, so must be inside Router)
          <OnboardingWizard />
        ) : (
          <>
            {user && <Header user={user} userProfile={userProfile} />}
            <div className="pageContent">
              <Routes>
                {user ? (
                  <>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/profile" element={<PlayerProfileForm />} />
                    <Route path="/user-diagnostic" element={<UserDiagnostic />} />
                    <Route path="/player-profile/:userId" element={<PlayerProfile />} />
                  <Route path="/player-profile/:userId/edit" element={<ProfileEdit />} />
                  <Route path="/talent-discovery" element={<TalentDiscovery />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/clubs/:clubId/templates" element={<TemplateManagementPage />} />
                    <Route path="/club/:clubId" element={<ClubLandingPage />} />
                    <Route path="/club/:clubId/edit" element={<ClubEditWizard />} />
                    <Route path="/club/:clubId/notifications" element={<ClubNotificationsPage />} />
                    <Route path="/clubs/:clubId" element={<ClubDetails />} />
                    <Route path="/clubs/:clubId/programs/:programId" element={<ProgramDetailsPage />} />
                    <Route path="/clubs/:clubId/programs/:programId/manage" element={<ProgramManage />} />
                    <Route path="/clubs/:clubId/programs/:programId/events" element={<EventsPage />} />
                    <Route path="/program/:programId" element={<ProgramLayout />}>
                      <Route path="events" element={<EventsPage />} />
                      <Route path="teams" element={<TeamsPage />} />
                      <Route path="events/new" element={<NewEventPage />} />
                      <Route path="equipment" element={<EquipmentManager />} />
                      <Route path="team-items" element={<TeamItemsPage />} />
                      <Route path="awards" element={<AwardsPage />} />
                      <Route path="*" element={<Navigate to="events" replace />} />
                    </Route>
                  </>
                ) : (
                  <>
                    <Route path="/login" element={<Login />} />
                    <Route path="*" element={<Login />} />
                  </>
                )}
              </Routes>
            </div>
          </>
        )}
      </div>
    </Router>
  );
}

function App() {
  return (
    <UserProvider>
      <NotificationProvider>
        <AppContent />
      </NotificationProvider>
    </UserProvider>
  );
}

export default App;
